import React from 'react';
import { X, ShoppingBag, ArrowRight, Minus, Plus, Trash2 } from 'lucide-react';

const CartDrawer = ({ isOpen, onClose, cart, onUpdateQuantity }) => {
  const total = cart.reduce((sum, item) => {
    const price = item.finalPrice || item.price || 0;
    return sum + (price * item.quantity);
  }, 0);

  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // WhatsApp Checkout — direct to store number
  const handleCheckout = () => {
    const phoneNumber = "96178872863"; // Lebanon number with country code

    let message = "Hello! I would like to order the following items from iSeries Store:\n\n";

    cart.forEach((item, index) => {
      const price = item.finalPrice || item.price || 0;
      const variant = item.selectedStorage && item.selectedColor
        ? ` (${item.selectedStorage}, ${item.selectedColor})`
        : '';
      message += `${index + 1}. ${item.name}${variant}\n`;
      message += `   Quantity: ${item.quantity}\n`;
      message += `   Price: $${(price * item.quantity).toLocaleString()}\n\n`;
    });

    message += `━━━━━━━━━━━━━━━━━━\n`;
    message += `Total Amount: $${total.toLocaleString()}\n`;
    message += `━━━━━━━━━━━━━━━━━━\n\n`;
    message += `Thank you!`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleRemove = (item) => {
    const itemId = item.cartId || item.id;

    if (!itemId) {
      console.error('Cannot remove item: no ID found', item);
      return;
    }

    try {
      if (window.trackRemoveFromCart) {
        window.trackRemoveFromCart(item);
      }
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }

    onUpdateQuantity(itemId, -999);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-500 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full md:max-w-[420px] bg-[#fbfbfd] shadow-[-20px_0_60px_-20px_rgba(0,0,0,0.3)] z-50 transform transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-6 py-5 md:px-7 flex items-center justify-between bg-white/80 backdrop-blur-xl border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">Your Bag</h2>
              {itemCount > 0 && (
                <span className="px-2.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded-full">
                  {itemCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2.5 text-gray-500 hover:text-[#1d1d1f] bg-gray-100/80 hover:bg-gray-200 rounded-full transition-all duration-300 active:scale-90"
              aria-label="Close cart"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <div className="w-20 h-20 rounded-3xl bg-white border border-gray-100 shadow-sm flex items-center justify-center mb-6">
                  <ShoppingBag className="w-9 h-9 text-gray-300" strokeWidth={1.2} />
                </div>
                <p className="text-xl font-bold text-[#1d1d1f] mb-2">Your bag is empty</p>
                <p className="text-sm text-gray-500 mb-6">
                  Looks like you haven't added anything yet.
                </p>
                <button
                  onClick={onClose}
                  className="px-7 py-3 bg-[#1d1d1f] text-white rounded-full text-sm font-semibold hover:bg-black transition-all duration-300 active:scale-95 shadow-lg"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              cart.map((item) => {
                const itemPrice = item.finalPrice || item.price || 0;
                const itemId = item.cartId || item.id;

                return (
                  <div
                    key={itemId}
                    className="flex gap-4 p-4 bg-white rounded-2xl border border-gray-100/80 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.06)]"
                  >
                    {/* Thumb */}
                    <div className="w-20 h-20 md:w-[88px] md:h-[88px] rounded-xl bg-[#f5f5f7] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="max-w-full max-h-full object-contain p-2"
                        />
                      ) : (
                        <div className={`w-14 h-14 ${item.colorClass || 'bg-gray-300'} rounded-lg`}></div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="text-[15px] font-semibold text-[#1d1d1f] leading-snug truncate">
                            {item.name}
                          </h4>
                          {(item.selectedStorage || item.selectedColor) ? (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {item.selectedStorage && (
                                <span className="text-[11px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">
                                  {item.selectedStorage}
                                </span>
                              )}
                              {item.selectedColor && (
                                <span className="text-[11px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md inline-flex items-center gap-1.5">
                                  {item.selectedHex && (
                                    <span
                                      className="w-2.5 h-2.5 rounded-full border border-black/10"
                                      style={{ backgroundColor: item.selectedHex }}
                                    />
                                  )}
                                  {item.selectedColor}
                                </span>
                              )}
                            </div>
                          ) : (
                            item.specs && (
                              <p className="text-xs text-gray-400 mt-1 truncate">{item.specs}</p>
                            )
                          )}
                        </div>

                        <button
                          onClick={() => handleRemove(item)}
                          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Remove from bag"
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        {/* Quantity stepper */}
                        <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                          <button
                            onClick={() => onUpdateQuantity(itemId, -1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:bg-white hover:text-[#1d1d1f] hover:shadow-sm transition-all active:scale-90"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-[#1d1d1f] tabular-nums">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => onUpdateQuantity(itemId, 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:bg-white hover:text-[#1d1d1f] hover:shadow-sm transition-all active:scale-90"
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </button>
                        </div>

                        <span className="text-[15px] font-bold text-orange-500 tabular-nums">
                          ${(itemPrice * item.quantity).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Summary + Checkout */}
          {cart.length > 0 && (
            <div className="p-5 md:p-6 bg-white border-t border-gray-100 space-y-5 shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.1)]">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                  <span className="tabular-nums">${total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-lg font-bold text-[#1d1d1f]">Total</span>
                  <span className="text-2xl font-bold text-[#1d1d1f] tabular-nums">
                    ${total.toLocaleString()}
                  </span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="group w-full py-4 text-[16px] rounded-2xl flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-[0_12px_28px_-8px_rgba(37,211,102,0.5)] hover:shadow-[0_16px_32px_-8px_rgba(37,211,102,0.6)] transition-all duration-300 active:scale-[0.98] font-bold"
              >
                Checkout on WhatsApp
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </button>

              <p className="text-center text-[11px] text-gray-400">
                Your order will be sent directly to our WhatsApp — no payment taken online.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
